import { useCallback, useEffect, useRef, useState } from 'react';

import ThumbNail from './ThumbNail';
import { fetchThumbnails } from '../../api/thumbnails';
import { useAuth } from '../../context/AuthContext';

const AUTOPLAY_MS = 6000;
const SWIPE_THRESHOLD = 50;
const RELEASE_DURATION = 300;
const API_BASE = (process.env.REACT_APP_API_BASE || 'http://localhost:8000').replace(/\/$/, '');

export default function Carousel({ onOpenAuth }) {
  const { isAuthenticated } = useAuth();
  const [thumbnails, setThumbnails] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isReleasing, setIsReleasing] = useState(false);
  const dragRef = useRef({ startX: 0, dragging: false });
  const thumbDragRef = useRef({ startX: 0, dragging: false, moved: false });
  const stageRef = useRef(null);
  const releaseTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchThumbnails()
      .then(({ thumbnails: items }) => {
        if (!cancelled) setThumbnails(items || []);
      })
      .catch(() => {
        if (!cancelled) setThumbnails([]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => clearTimeout(releaseTimerRef.current), []);

  const count = thumbnails.length;

  const goTo = useCallback(
    (index) => {
      if (count === 0) return;
      setActiveIndex(((index % count) + count) % count);
    },
    [count]
  );

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => {
      setActiveIndex((current) => (current + 1) % count);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [count, activeIndex]);

  const handlePointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    if (e.target.closest('a, button')) return;
    clearTimeout(releaseTimerRef.current);
    dragRef.current = { startX: e.clientX, dragging: true };
    setIsReleasing(false);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    setDragOffset(e.clientX - dragRef.current.startX);
  }, []);

  const handlePointerUp = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    const offset = e.clientX - dragRef.current.startX;
    dragRef.current.dragging = false;

    if (Math.abs(offset) >= SWIPE_THRESHOLD) {
      const stageWidth = stageRef.current?.clientWidth ?? 0;
      setIsReleasing(true);
      setDragOffset(offset < 0 ? -stageWidth : stageWidth);
      releaseTimerRef.current = setTimeout(() => {
        goTo(activeIndex + (offset < 0 ? 1 : -1));
        setDragOffset(0);
        setIsReleasing(false);
      }, RELEASE_DURATION);
    } else {
      setIsReleasing(true);
      setDragOffset(0);
      releaseTimerRef.current = setTimeout(() => setIsReleasing(false), RELEASE_DURATION);
    }
  }, [activeIndex, goTo]);

  const handleThumbPointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    thumbDragRef.current = { startX: e.clientX, dragging: true, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const handleThumbPointerMove = useCallback((e) => {
    if (!thumbDragRef.current.dragging) return;
    if (Math.abs(e.clientX - thumbDragRef.current.startX) > 10) {
      thumbDragRef.current.moved = true;
    }
  }, []);

  const handleThumbPointerUp = useCallback((e) => {
    const drag = thumbDragRef.current;
    if (!drag.dragging) return;
    const offset = e.clientX - drag.startX;
    drag.dragging = false;

    if (Math.abs(offset) >= SWIPE_THRESHOLD) {
      goTo(activeIndex + (offset < 0 ? 1 : -1));
    }
  }, [activeIndex, goTo]);

  const handleThumbClick = useCallback((index) => {
    if (thumbDragRef.current.moved) {
      thumbDragRef.current.moved = false;
      return;
    }
    goTo(index);
  }, [goTo]);

  const isDragActive = dragOffset !== 0 || isReleasing;
  const stageWidth = stageRef.current?.clientWidth ?? 0;
  const incomingIndex = isDragActive && count > 1
    ? dragOffset <= 0
      ? (activeIndex + 1) % count
      : (activeIndex - 1 + count) % count
    : null;

  const getSlideStyle = (index) => {
    if (!isDragActive) return undefined;

    const transition = isReleasing ? `transform ${RELEASE_DURATION}ms ease` : 'none';

    if (index === activeIndex) {
      return { transform: `translateX(${dragOffset}px)`, transition, opacity: 1 };
    }
    if (index === incomingIndex) {
      const edge = dragOffset <= 0 ? stageWidth : -stageWidth;
      return { transform: `translateX(${edge + dragOffset}px)`, transition, opacity: 1 };
    }
    return { opacity: 0, transition: 'none' };
  };

  if (count === 0) {
    return <div className="carousel-stage" />;
  }

  return (
    <div className="carousel-wrap">
      <div
        ref={stageRef}
        className="carousel-stage"
        style={{ touchAction: 'none', userSelect: 'none', cursor: isDragActive ? 'grabbing' : 'grab' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {thumbnails.map((item, index) => (
          <div
            key={item.id}
            className={`slide${index === activeIndex ? ' active' : ''}`}
            style={getSlideStyle(index)}
          >
            <div
              className="slide-bg"
              style={
                item.image
                  ? { backgroundImage: `url(${API_BASE + item.image})` }
                  : { background: `linear-gradient(135deg, ${item.bg_from}, ${item.bg_mid}, ${item.bg_to})` }
              }
            />
            <div className="slide-overlay" />
            <div className="slide-accent-line" style={{ background: '#7EBC51' }} />
            <div className="slide-content" style={{ '--accent': item.bg_to }}>
              <h2 className="slide-title">{item.name}</h2>
              <p className="slide-desc">{item.description}</p>
              <div className="slide-buttons">
                {isAuthenticated ? (
                  <a className="btn-play" href={item.url} target="_blank" rel="noopener noreferrer">
                    &#9658; Visit Site
                  </a>
                ) : (
                  <a
                    className="btn-play"
                    href={item.url}
                    onClick={(e) => {
                      e.preventDefault();
                      onOpenAuth('signup');
                    }}
                  >
                    &#9658; Sign up
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}

        {count > 1 && (
          <>

            <div className="slide-counter">
              {thumbnails.map((item, index) => (
                <span
                  key={item.id}
                  className={`counter-pip${index === activeIndex ? ' active' : ''}`}
                  onClick={() => goTo(index)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {count > 1 && (

        <div
          className="thumbnails"
          style={{ touchAction: 'none', userSelect: 'none' }}
          onPointerDown={handleThumbPointerDown}
          onPointerMove={handleThumbPointerMove}
          onPointerUp={handleThumbPointerUp}
          onPointerCancel={handleThumbPointerUp}
        >
          {thumbnails.map((item, index) => {
            let offset = ((index - activeIndex) % count + count) % count;
            if (offset > count / 2) offset -= count;
            return (
              <ThumbNail
                key={item.id}
                item={item}
                active={index === activeIndex}
                duration={AUTOPLAY_MS}
                offset={offset}
                onClick={() => handleThumbClick(index)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}